/**
 * Per-section LLM feedback panel for reading/listening modules.
 *
 * Renders one card per section with Strengths / Weaknesses / Notes blocks —
 * matching the W/S "MORE INFORMATION" visual language so the four-skill
 * results-review UX feels consistent.
 *
 * - Shows a spinner row while any section is still `pending`.
 * - Hides cleanly if the feature is off or no rows have been enqueued.
 *
 * @module SectionFeedbackPanel
 */
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import type { ApiSectionFeedback } from "@/lib/simulationResultsApi";

interface Props {
  readonly feedback: readonly ApiSectionFeedback[];
  /** When true, panel is rendered inside the scroll body below question cards. */
  readonly embedded?: boolean;
}

export function SectionFeedbackCard({ row }: { readonly row: ApiSectionFeedback }) {
  const { t } = useTranslation();

  if (row.status === "pending") {
    return (
      <div className="rounded-lg border border-border/50 bg-secondary/5 p-4">
        <div className="mb-2 flex items-center justify-between">
          <Label className="font-ui text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
            {t("simulation.results.reviewDialog.section", {
              defaultValue: "Section {{number}}",
              number: row.section,
            })}
          </Label>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            {t("simulation.results.reviewDialog.analysing", {
              defaultValue: "Analysing…",
            })}
          </div>
        </div>
        <p className="font-ui text-xs italic text-muted-foreground">
          {t("simulation.results.reviewDialog.analysingHint", {
            defaultValue:
              "AI is reviewing your answers for this section. This usually takes a few seconds.",
          })}
        </p>
      </div>
    );
  }

  if (row.status === "failed") {
    return (
      <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
        <Label className="font-ui mb-1 block text-[10px] font-bold uppercase tracking-[0.2em] text-destructive">
          {t("simulation.results.reviewDialog.section", {
            defaultValue: "Section {{number}}",
            number: row.section,
          })}
        </Label>
        <p className="font-ui text-xs text-destructive/80">
          {t("simulation.results.reviewDialog.analysisFailed", {
            defaultValue: "Section analysis is temporarily unavailable.",
          })}
        </p>
      </div>
    );
  }

  const priorityFocus = (row.priority_focus || "").trim();
  const practice = row.recommended_practice ?? [];
  const strength = (row.strength_summary || "").trim();
  const weakness = (row.weakness_summary || "").trim();
  const notes = (row.notes_summary || "").trim();
  if (!priorityFocus && !practice.length && !strength && !weakness && !notes) return null;

  return (
    <div className="rounded-lg border border-border/50 bg-background p-4">
      <Label className="font-ui mb-3 block text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
        {t("simulation.results.reviewDialog.section", {
          defaultValue: "Section {{number}}",
          number: row.section,
        })}
      </Label>
      {priorityFocus && (
        <div className="mb-3 rounded-lg border border-primary/20 bg-primary/5 p-3">
          <div className="mb-1.5 border-l-2 border-primary px-1 text-[10px] font-bold uppercase tracking-widest text-primary">
            {t("simulation.results.reviewDialog.priorityFocus", {
              defaultValue: "Priority focus",
            })}
          </div>
          <p className="font-ui text-xs leading-relaxed text-foreground/80 whitespace-pre-wrap">
            {priorityFocus}
          </p>
        </div>
      )}
      {practice.length > 0 && (
        <div className="mb-3 rounded-lg border border-border/50 bg-muted/20 p-3">
          <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            {t("simulation.results.reviewDialog.recommendedPractice", {
              defaultValue: "Recommended practice",
            })}
          </div>
          <ul className="font-ui list-inside list-disc space-y-1 text-xs text-foreground/80">
            {practice.map((item) => (
              <li key={item.id}>
                <span className="font-medium text-foreground">{item.title}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {strength && (
          <div className="rounded-lg border border-success/10 bg-success/5 p-3">
            <div className="mb-1.5 border-l-2 border-success px-1 text-[10px] font-bold uppercase tracking-widest text-success">
              {t("simulation.results.reviewDialog.strengths", {
                defaultValue: "Strengths",
              })}
            </div>
            <div className="font-ui text-xs leading-relaxed text-foreground/70 whitespace-pre-wrap">
              {strength}
            </div>
          </div>
        )}
        {weakness && (
          <div className="rounded-lg border border-destructive/10 bg-destructive/5 p-3">
            <div className="mb-1.5 border-l-2 border-destructive px-1 text-[10px] font-bold uppercase tracking-widest text-destructive">
              {t("simulation.results.reviewDialog.weaknesses", {
                defaultValue: "Weaknesses",
              })}
            </div>
            <div className="font-ui text-xs leading-relaxed text-foreground/70 whitespace-pre-wrap">
              {weakness}
            </div>
          </div>
        )}
        {notes && (
          <div
            className={cn(
              "rounded-lg border border-border/50 bg-muted/30 p-3",
              !strength || !weakness ? "" : "sm:col-span-2",
            )}
          >
            <div className="mb-1.5 border-l-2 border-primary px-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              {t("simulation.results.reviewDialog.notes", {
                defaultValue: "Notes",
              })}
            </div>
            <div className="font-ui text-xs leading-relaxed text-foreground/70 whitespace-pre-wrap">
              {notes}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function SectionFeedbackPanel({ feedback, embedded = false }: Props) {
  const { t } = useTranslation();

  if (!feedback || feedback.length === 0) return null;

  // Skip the panel entirely if every row is empty/complete-but-blank.
  const hasContent = feedback.some(
    (r) =>
      r.status === "pending" ||
      r.status === "failed" ||
      (r.priority_focus || "").trim().length > 0 ||
      (r.recommended_practice?.length ?? 0) > 0 ||
      (r.strength_summary || "").trim().length > 0 ||
      (r.weakness_summary || "").trim().length > 0 ||
      (r.notes_summary || "").trim().length > 0,
  );
  if (!hasContent) return null;

  return (
    <div
      className={cn(
        embedded
          ? "mt-8 border-t border-border/60 pt-6"
          : "border-b border-border/60 bg-muted/15 px-4 py-4 sm:px-6",
      )}
    >
      <Label className="font-ui mb-3 block text-center text-xs font-bold uppercase tracking-[0.2em] text-primary">
        {t("simulation.results.reviewDialog.moreInformation", {
          defaultValue: "More Information",
        })}
      </Label>
      <div className="space-y-3">
        {[...feedback]
          .sort((a, b) => a.section - b.section)
          .map((row) => (
            <SectionFeedbackCard key={row.section} row={row} />
          ))}
      </div>
    </div>
  );
}
